import { css } from '@emotion/react';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AsyncProgress from './AsyncProgress';

import { useState } from 'react';
import cancelRun from 'store/cancelRun';

import IRunEntry from 'types/IRunEntry';

type IStatus = 'INIT' | 'PENDING' | 'ERROR' | 'SUCCESS';

interface ICancelAsync {
  status: IStatus;
  run: IRunEntry;
}

interface Props {
  onClose: () => void;
  selectedRuns: IRunEntry[];
}

export default function CancelConfirmModal(props: Props) {
  const { onClose, selectedRuns } = props;

  // Captured at mount only — parent mounts this when the user opens the
  // modal, and we want to keep showing rows for runs even after they're
  // cancelled and disappear from the parent's selectedRuns.
  const [cancelAsyncList, setCancelAsyncList] = useState<ICancelAsync[]>(() =>
    selectedRuns.map((run) => ({ status: 'INIT', run })),
  );

  function cancelRuns() {
    cancelAsyncList
      .filter((async) => ['INIT', 'ERROR'].includes(async.status))
      .forEach((async) => {
        function setStatus(status: IStatus) {
          setCancelAsyncList((list) =>
            list.map((some) =>
              some.run.id !== async.run.id ? some : { ...async, status },
            ),
          );
        }

        setStatus('PENDING');
        cancelRun(async.run.id).then(
          () => setStatus('SUCCESS'),
          () => setStatus('ERROR'),
        );
      });
  }

  const somePending = cancelAsyncList.some(
    (async) => async.status === 'PENDING',
  );
  const someFailed = cancelAsyncList.some((async) => async.status === 'ERROR');
  const allDone = cancelAsyncList.every((async) => async.status === 'SUCCESS');
  const someRunning = cancelAsyncList.some(
    (async) => async.run.state === 'RUNNING',
  );

  return (
    <Modal open onClose={onClose}>
      <Paper
        elevation={2}
        css={css`
          padding: 48px;

          position: fixed;
          top: 20%;
          left: 50%;
          transform: translateX(-50%);
          width: 400px;
          max-width: 100%;
        `}
      >
        <Typography component="h1" variant="h6">
          Cancel
        </Typography>
        <br />
        <Typography variant="body1">
          Are you sure you want to cancel the {cancelAsyncList.length} selected{' '}
          {cancelAsyncList.length === 1 ? 'workflow' : 'workflows'}?
        </Typography>

        {someRunning && (
          <Alert
            severity="info"
            css={css`
              margin-top: 16px;
            `}
          >
            For immediate cancellation of running tasks the worker must run
            pytailor &gt;= 0.11.0. On older workers, running tasks will run
            until complete.
          </Alert>
        )}

        <br />
        <ul
          css={css`
            margin: 0;
            padding: 0;

            list-style: none;

            li {
              margin-top: 8px;
            }

            max-height: 300px;
            overflow-y: auto;
          `}
        >
          {cancelAsyncList.map((cancelAsync) => (
            <li key={cancelAsync.run.id}>
              <AsyncProgress status={cancelAsync.status}>
                {cancelAsync.run.name}
              </AsyncProgress>
            </li>
          ))}
        </ul>

        <br />
        <div
          css={css`
            display: flex;
            justify-content: flex-end;

            button ~ button {
              margin-left: 16px;
            }
          `}
        >
          <Button onClick={onClose} disabled={allDone}>
            Dismiss
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={somePending}
            onClick={allDone ? onClose : cancelRuns}
          >
            {someFailed
              ? 'Try again'
              : allDone
                ? 'Close'
                : 'Cancel workflows'}
          </Button>
        </div>
      </Paper>
    </Modal>
  );
}
